import { addToCart } from '@theme/cart-api';
import { OverlayDrawer } from '@theme/overlay-drawer';

/**
 * @typedef {Object} QuickAddSheetRefs
 * @property {HTMLElement} [panel]
 * @property {HTMLElement} [thumb]
 * @property {HTMLElement} [vendor]
 * @property {HTMLAnchorElement} [title]
 * @property {HTMLElement} [price]
 * @property {HTMLElement} [body]
 */

/** @typedef {import('./product-card.js').QuickAddProduct} QuickAddProduct */

/**
 * Quick add on phones (snippets/site-overlays.liquid): a bottom sheet with large sizes, filled from
 * the tapped product card by `<product-card>` through `openFor()`. Choosing a size closes the sheet
 * and adds the piece; the cart drawer takes over.
 *
 * Markup: `ref="panel"` on the panel, `ref="thumb" | "vendor" | "title" | "price" | "body"` for the
 * product summary and the sizes slot, close button `on:click="/close"`. The cloned sizes form keeps
 * its `on:submit="/addToCart"`, which resolves to this element.
 */
export class QuickAddSheet extends OverlayDrawer {
  /** @returns {QuickAddSheetRefs} */
  get sheetRefs() {
    return /** @type {QuickAddSheetRefs} */ (this.refs);
  }

  /** @param {QuickAddProduct} product */
  openFor(product) {
    const { thumb, vendor, title, price, body } = this.sheetRefs;

    if (thumb) {
      if (product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = product.title;
        thumb.replaceChildren(image);
      } else {
        thumb.replaceChildren();
      }
    }
    if (vendor) vendor.textContent = product.vendor;
    if (title) {
      title.textContent = product.title;
      title.href = product.url;
    }
    if (price) {
      const nodes = product.price ? Array.from(product.price.childNodes, (node) => node.cloneNode(true)) : [];
      price.replaceChildren(...nodes);
    }
    if (body) {
      const form = /** @type {HTMLFormElement} */ (product.form.cloneNode(true));
      form.removeAttribute('id');
      form.classList.remove('is-done');
      body.replaceChildren(form);
    }

    this.open(product.opener);
  }

  /** @param {SubmitEvent} event */
  async addToCart(event) {
    event.preventDefault();
    const button = event.submitter;
    if (!(button instanceof HTMLButtonElement) || button.name !== 'id' || button.disabled) return;
    const form = event.target instanceof HTMLFormElement ? event.target : null;

    // The cart drawer opens next: no focus return to the card
    this.close(false);
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    const sections = await addToCart([{ id: Number(button.value), quantity: 1 }], { source: 'quick-add-sheet' });
    button.classList.remove('is-loading');
    button.removeAttribute('aria-busy');
    if (sections) form?.classList.add('is-done');
  }
}

if (!customElements.get('quick-add-sheet')) {
  customElements.define('quick-add-sheet', QuickAddSheet);
}
